import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { apiName, fileExtension } from './analytics-helpers';
import { AnalyticsService } from './analytics.service';

/**
 * Counts every report and export download in one place: any successful API response fetched as a
 * file (blob / arraybuffer) becomes a GA4 file_download event named after its endpoint, e.g.
 * { export_name: "ExportReportInvoices", file_extension: "xlsx" }. No per-screen code needed, and
 * new exports are counted automatically. Does nothing where analytics is off.
 */
@Injectable()
export class AnalyticsInterceptor implements HttpInterceptor {
  constructor(private analytics: AnalyticsService) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isFile = req.responseType === 'blob' || req.responseType === 'arraybuffer';
    const name = isFile ? apiName(req.url) : null;
    if (!name) {
      return next.handle(req);
    }
    return next.handle(req).pipe(tap(event => {
      if (event instanceof HttpResponse && event.ok && this.isFile(event)) {
        this.analytics.event('file_download', {
          export_name: name,
          file_extension: fileExtension(event.headers.get('Content-Disposition'), event.headers.get('Content-Type')) ?? undefined,
        });
      }
    }));
  }

  // The generated API client asks for blobs on every call and parses JSON itself, so only a
  // response that is not JSON is a file.
  private isFile(response: HttpResponse<unknown>): boolean {
    const type = (response.headers.get('Content-Type') || '').toLowerCase();
    return !!type && !type.includes('json') && !type.startsWith('text/plain');
  }
}
